import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import CustomerPrimaryButton from './CustomerPrimaryButton';
import { CUSTOMER_COLORS } from '../../constants/customerTheme';
import { formatCurrency } from '../../utils/currencyUtils';

/**
 * Shown before paying a booking whose terms were not accepted while booking, e.g. one that was
 * confirmed on request or offered from the waitlist.
 */
const AppointmentPaymentTermsDialog = ({ open, terms, loading, failed, currencyCode, onClose, onAccept }) => {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (open) setAccepted(false);
  }, [open, terms?.policyText]);

  const requiresAcceptance = Boolean(terms && terms.nonRefundableAmount > 0);

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      showCloseButton
      dividers
      title={t('customer.appointments.myAppointments.payTerms.title')}
      actionsSx={{ justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
          <CustomerPrimaryButton
            onClick={onAccept}
            disabled={loading || failed || !terms || (requiresAcceptance && !accepted)}
          >
            {t('customer.appointments.myAppointments.payTerms.continue')}
          </CustomerPrimaryButton>
        </>
      )}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} sx={{ color: CUSTOMER_COLORS.primary }} />
          <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.policy.loading')}</Typography>
        </Box>
      ) : null}
      {failed ? <Alert severity="warning">{t('customer.appointments.booking.policy.error')}</Alert> : null}
      {terms && !loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('customer.appointments.myAppointments.payTerms.amount', {
              amount: formatCurrency(terms.onlineAmount, terms.currency || currencyCode),
            })}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{terms.policyText}</Typography>
          {requiresAcceptance ? (
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mr: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={accepted}
                  onChange={(event) => setAccepted(event.target.checked)}
                  sx={{ pt: 0.25 }}
                />
              )}
              label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t('customer.appointments.booking.policy.accept')}</Typography>}
            />
          ) : null}
        </Box>
      ) : null}
    </ScrollableDialog>
  );
};

export default AppointmentPaymentTermsDialog;
